import { executeApiKeyGraphql } from '../amplifyClient';

export type PublicApplySettings = {
  organization?: {
    name?: string | null;
    shortName?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    address?: unknown;
    phone?: string | null;
    phoneExt?: string | null;
    emailFinancialAid?: string | null;
    emailAdmissions?: string | null;
    emailSupport?: string | null;
    website?: string | null;
    missionStatement?: string | null;
    timezone?: string | null;
    logo?: string | null;
  } | null;
  academicYear?: {
    label?: string | null;
    labelOverride?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  } | null;
  divisions?: Array<{
    id?: string | null;
    type?: string | null;
    displayName?: string | null;
    enabled?: boolean | null;
  } | null> | null;
  admissionDates?: {
    openDate?: string | null;
    closeDate?: string | null;
    decisionDate?: string | null;
    status?: string | null;
  } | null;
  admissionConfig?: {
    requiredDocuments?: Array<{
      id?: string | null;
      label?: string | null;
      required?: boolean | null;
      visible?: boolean | null;
      requirementMode?: string | null;
      gradeScope?: Array<string | null> | null;
      description?: string | null;
      scope?: string | null;
    } | null> | null;
    transcriptsRequired?: boolean | null;
    transcriptGrades?: Array<string | null> | null;
    applicationAgreements?: Array<{
      id?: string | null;
      label?: string | null;
      text?: string | null;
      required?: boolean | null;
      visible?: boolean | null;
      requirementMode?: string | null;
    } | null> | null;
    vaccinationPolicy?: {
      allowReligiousExemptions?: boolean | null;
      requireBothParentsAcknowledgment?: boolean | null;
      policyText?: string | null;
    } | null;
    interviewScheduling?: {
      familySelfSchedulingEnabled?: boolean | null;
    } | null;
  } | null;
  fees?: Array<{
    id?: string | null;
    name?: string | null;
    amount?: number | null;
  } | null> | null;
  tuitionRates?: Array<{
    id?: string | null;
    gradeLabel?: string | null;
    gender?: string | null;
    amount?: number | null;
  } | null> | null;
  financialAid?: {
    cycle?: {
      schoolYear?: string | null;
      openDate?: string | null;
      onTimeDeadline?: string | null;
      hardCutoffDeadline?: string | null;
      lateFeeAmount?: number | null;
      submissionRequirement?: string | null;
    } | null;
    cycles?: Record<string, {
      schoolYear?: string | null;
      openDate?: string | null;
      onTimeDeadline?: string | null;
      hardCutoffDeadline?: string | null;
      lateFeeAmount?: number | null;
      submissionRequirement?: string | null;
    } | null> | Array<{
      schoolYear?: string | null;
      openDate?: string | null;
      onTimeDeadline?: string | null;
      hardCutoffDeadline?: string | null;
      lateFeeAmount?: number | null;
      submissionRequirement?: string | null;
    } | null> | null;
    requiredDocuments?: Array<{
      id?: string | null;
      label?: string | null;
      enabled?: boolean | null;
    } | null> | null;
  } | null;
  parentResources?: Array<{
    title?: string | null;
    type?: string | null;
    date?: string | null;
  } | null> | null;
  contactInfo?: {
    email?: string | null;
    phone?: string | null;
    address?: unknown;
    hours?: string | null;
    extensions?: unknown;
    divisionContacts?: unknown;
  } | null;
};

type PublicApplySettingsQueryOptions = {
  includeOrganizationFinancialAidEmail: boolean;
  includeFinancialAidCycles: boolean;
  includeInterviewScheduling: boolean;
};

const buildPublicApplySettingsQuery = ({
  includeOrganizationFinancialAidEmail,
  includeFinancialAidCycles,
  includeInterviewScheduling,
}: PublicApplySettingsQueryOptions): string => /* GraphQL */ `
  query PublicApplySettings {
    getPublicApplySettings {
      organization {
        name
        shortName
        primaryColor
        secondaryColor
        address
        phone
        phoneExt
${includeOrganizationFinancialAidEmail ? '        emailFinancialAid\n' : ''}
        emailAdmissions
        emailSupport
        website
        missionStatement
        timezone
        logo
      }
      academicYear {
        label
        labelOverride
        startDate
        endDate
      }
      divisions {
        id
        type
        displayName
        enabled
      }
      admissionDates {
        openDate
        closeDate
        decisionDate
        status
      }
      admissionConfig {
        requiredDocuments {
          id
          label
          required
          visible
          requirementMode
          gradeScope
          description
          scope
        }
        transcriptsRequired
        transcriptGrades
        applicationAgreements {
          id
          label
          text
          required
          visible
          requirementMode
        }
        vaccinationPolicy {
          allowReligiousExemptions
          requireBothParentsAcknowledgment
          policyText
        }
${includeInterviewScheduling ? `        interviewScheduling {
          familySelfSchedulingEnabled
        }
` : ''}
      }
      fees {
        id
        name
        amount
      }
      tuitionRates {
        id
        gradeLabel
        gender
        amount
      }
      financialAid {
        cycle {
          schoolYear
          openDate
          onTimeDeadline
          hardCutoffDeadline
          lateFeeAmount
        }
${includeFinancialAidCycles ? '        cycles\n' : ''}
        requiredDocuments {
          id
          label
          enabled
        }
      }
      parentResources {
        title
        type
        date
      }
      contactInfo {
        email
        phone
        address
        hours
        extensions
        divisionContacts
      }
    }
  }
`;

type PublicApplySettingsGraphqlResponse = {
  data?: {
    getPublicApplySettings?: PublicApplySettings | null;
  } | null;
};

const isMissingPublicFieldError = (
  error: unknown,
  parentType: string,
  fieldName: string,
): boolean => {
  const messages: string[] = [];
  const collectMessages = (value: unknown) => {
    if (!value) return;
    if (value instanceof Error) {
      messages.push(value.message);
      collectMessages((value as Error & { cause?: unknown }).cause);
      return;
    }
    if (typeof value === 'string') {
      messages.push(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(collectMessages);
      return;
    }
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      collectMessages(record.message);
      collectMessages(record.errors);
    }
  };

  collectMessages(error);
  const fieldToken = String.raw`["'“”]?${fieldName}["'“”]?`;
  return messages.some((message) =>
    new RegExp(parentType, 'i').test(message)
    && (
      (/FieldUndefined/i.test(message) && new RegExp(String.raw`Field\s+${fieldToken}`, 'i').test(message))
      || new RegExp(String.raw`Cannot query field\s+${fieldToken}`, 'i').test(message)
      || new RegExp(String.raw`Unknown field\s+${fieldToken}`, 'i').test(message)
      || new RegExp(String.raw`Field\s+${fieldToken}\b[\s\S]*?\b(?:not\s+defined|unknown|undefined)`, 'i').test(message)
      || new RegExp(String.raw`${fieldToken}\s+(?:is\s+)?not\s+defined`, 'i').test(message)
    )
  );
};

const isMissingPublicFinancialAidCyclesFieldError = (error: unknown): boolean =>
  isMissingPublicFieldError(error, 'PublicApplySettingsFinancialAid', 'cycles');

const isMissingPublicInterviewSchedulingFieldError = (error: unknown): boolean =>
  isMissingPublicFieldError(error, 'PublicApplySettingsAdmissionConfig', 'interviewScheduling');

const isMissingPublicOrganizationFinancialAidEmailFieldError = (error: unknown): boolean =>
  isMissingPublicFieldError(error, 'PublicApplySettingsOrganization', 'emailFinancialAid');

const executePublicApplySettingsQuery = async (query: string): Promise<PublicApplySettings> => {
  const result = await executeApiKeyGraphql<PublicApplySettingsGraphqlResponse>({
    query,
  });
  const settings = result.data?.getPublicApplySettings;
  if (!settings || typeof settings !== 'object') {
    throw new Error('Public apply settings query returned no settings projection.');
  }
  return settings;
};

export const getPublicApplySettings = async (): Promise<PublicApplySettings> => {
  let includeOrganizationFinancialAidEmail = true;
  let includeFinancialAidCycles = true;
  let includeInterviewScheduling = true;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      return await executePublicApplySettingsQuery(
        buildPublicApplySettingsQuery({
          includeOrganizationFinancialAidEmail,
          includeFinancialAidCycles,
          includeInterviewScheduling,
        }),
      );
    } catch (error) {
      if (
        includeOrganizationFinancialAidEmail
        && isMissingPublicOrganizationFinancialAidEmailFieldError(error)
      ) {
        includeOrganizationFinancialAidEmail = false;
        console.warn(
          '[public-settings] Retrying public apply settings without organization.emailFinancialAid because the deployed schema does not expose that field yet.',
          error,
        );
        continue;
      }
      if (
        includeInterviewScheduling
        && isMissingPublicInterviewSchedulingFieldError(error)
      ) {
        includeInterviewScheduling = false;
        console.warn(
          '[public-settings] Retrying public apply settings without admissionConfig.interviewScheduling because the deployed schema does not expose that field yet.',
          error,
        );
        continue;
      }
      if (
        includeFinancialAidCycles
        && isMissingPublicFinancialAidCyclesFieldError(error)
      ) {
        includeFinancialAidCycles = false;
        console.warn(
          '[public-settings] Retrying public apply settings without financialAid.cycles because the deployed schema does not expose that field yet.',
          error,
        );
        continue;
      }
      throw error;
    }
  }

  throw new Error('Public apply settings query could not find a compatible schema projection.');
};
